import {HttpClient} from '@angular/common/http';
import {computed, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Clinician} from '@domain/clinician/clinician';
import {Invitation} from '@domain/clinician/invitation';
import {Status} from '@domain/response-status';
import {UserState} from '@domain/user-state';
import {RequestStatus} from '@infrastructure/request-status.enum';
import {TranslateService} from '@ngx-translate/core';
import {SnackBarService} from '@shared/snackbar/snackbar.service';
import {catchError, EMPTY, map, merge, Subject, switchMap, tap} from 'rxjs';

interface ClinicianState extends UserState<Clinician>
{
    invitations: Invitation[];
}

interface InvitationBasePayload
{
    clinicId: string;
    specialtyId: string;
}

interface DeclineInvitationPayload extends InvitationBasePayload
{
    message: string;
}

@Injectable({
    providedIn: 'root'
})
export class ClinicianService
{
    private readonly httpClient = inject(HttpClient);

    private readonly snackBar = inject(SnackBarService);

    private readonly translate = inject(TranslateService);

    private readonly state = signal<ClinicianState>({
        status: RequestStatus.NOT_EXECUTED,
        user: null,
        invitations: []
    });

    private readonly emailState = signal<Status>({
        status: RequestStatus.NOT_EXECUTED
    });

    public readonly clinician = computed(() => this.state().user);

    public readonly specialities = computed(() => this.state().user.specialties);

    public readonly loading = computed(() => this.state().status === RequestStatus.PENDING);

    public readonly completed = computed(() => this.state().status === RequestStatus.CONCLUDED);

    public readonly emailSendCompleted = computed(() => this.emailState().status === RequestStatus.CONCLUDED);

    public readonly emailSendPending = computed(() => this.emailState().status === RequestStatus.PENDING);

    public readonly invitations = computed(() => this.state().invitations);

    private readonly getDetails$ = new Subject<void>();

    public readonly clinicianFetched$ = this.getDetails$.pipe(
        switchMap(() =>
        {
            return this.httpClient.get('clinician/profile').pipe(
                map(Clinician.fromJSON),
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR,
                        user: null
                    });

                    return EMPTY;
                })
            );
        })
    );

    private readonly update$ = new Subject<Record<string, any>>();

    private readonly clinicianUpdated$ = this.update$.pipe(
        switchMap((clinician) =>
        {
            return this.httpClient.patch('clinician/profile', clinician).pipe(
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR,
                        user: this.clinician()
                    });
                    
                    this.snackBar.showErrorSnackbar(this.translate.instant('PROFILE.ERROR_MESSAGE'));

                    return EMPTY;
                }),
                tap(() =>
                {
                    this.state.set({
                        ...this.state(),
                        user: new Clinician(
                            this.clinician().id,
                            this.clinician().email,
                            clinician.doctorNumber,
                            this.clinician().specialties,
                            clinician.firstName,
                            clinician.lastName,
                            clinician.picture,
                            clinician.description
                        )
                    });
                }));
        })
    );

    private readonly getPendingInvitations$ = new Subject();

    private readonly fetchedPendingInvitations$ = this.getPendingInvitations$.pipe(
        switchMap(() =>
        {
            return this.httpClient.get('clinician/clinic-invitation').pipe(
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR
                    });
                        
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.GENERIC'));
                        
                    return EMPTY;
                }),
                map((invitationsResponse: Record<string, any>[]) => invitationsResponse.map(Invitation.fromJSON))
            );
        })
    );

    private readonly dismissInvitation$ = new Subject<DeclineInvitationPayload>();

    private readonly invitationDismissed$ = this.dismissInvitation$.pipe(
        switchMap(({clinicId, specialtyId, message}) =>
        {
            return this.httpClient.delete('clinician/decline-invitation', {body: {
                clinicId,
                message,
                specialityId: specialtyId
            }}).pipe(
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR
                    });
                        
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.EMAIL_SEND_NOT_COMPLETE'));
                        
                    return EMPTY;
                })
            );
        })
    );

    private readonly updateSpecialityInfo$ = new Subject<InvitationBasePayload>();

    private readonly specialityInfoUpdated$ = this.updateSpecialityInfo$.pipe(
        switchMap(({clinicId, specialtyId}) =>
        {
            return this.httpClient.post('clinician/update-speciality', {
                clinicId,
                specialityId: specialtyId
            }).pipe(
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR
                    });
                        
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.RESPONSE_NOT_SUBMITTED'));
                        
                    return EMPTY;
                }),
                tap(() => this.snackBar.showSuccessSnackbar(this.translate.instant('GENERAL.CONFIGURATIONS.RESPONSE_SUCCESS.CONFIGURATIONS_UPDATED_SUCCESSFULLY')))
            );
        })
    );

    private readonly updateAppointmentTax$ = new Subject<{tax: number}>();

    private readonly updatedAppointmentTax$ = this.updateAppointmentTax$.pipe(
        switchMap(({tax}) =>
        {
            return this.httpClient.post('clinician/update-appointment-tax', {tax}).pipe(
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR
                    });
                        
                    this.snackBar.showErrorSnackbar(this.translate.instant('GENERAL.RESPONSE_ERRORS.APPOINTMENT_TAX_NOT_UPDATED'));
                        
                    return EMPTY;
                }),
                tap(() => this.snackBar.showSuccessSnackbar(this.translate.instant('GENERAL.CONFIGURATIONS.RESPONSE_SUCCESS.CONFIGURATIONS_UPDATED_SUCCESSFULLY')))
            );
        })
    );

    public constructor()
    {
        this.clinicianFetched$
            .pipe(takeUntilDestroyed())
            .subscribe({
                next: (user: Clinician) =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.CONCLUDED,
                        user
                    });
                }
            });

        this.clinicianUpdated$
            .pipe(takeUntilDestroyed())
            .subscribe({
                next: () =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.CONCLUDED
                    });

                    this.snackBar.showSuccessSnackbar(this.translate.instant('PROFILE.SUCCESS_MESSAGE'));
                }
            });
        
        this.fetchedPendingInvitations$
            .pipe(takeUntilDestroyed())
            .subscribe({
                next: (invitations: Invitation[]) =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.CONCLUDED,
                        invitations
                    });
                }
            });

        merge(this.invitationDismissed$, this.specialityInfoUpdated$).pipe(takeUntilDestroyed())
            .subscribe({
                next: () =>
                {
                    this.emailState.set({
                        status: RequestStatus.CONCLUDED
                    });

                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.CONCLUDED,
                        invitations: this.state().invitations.slice(1)
                    });

                    this.getClinicianDetails();
                }
            });

        this.updatedAppointmentTax$
            .pipe(takeUntilDestroyed())
            .subscribe({
                next: () =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.CONCLUDED
                    });
                }
            });
    }
    
    public getClinicianDetails(): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.getDetails$.next();
    }

    public updateClinician(clinician: Record<string, any>): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.update$.next(clinician);
    }

    public checkForPendingInvitations(): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.getPendingInvitations$.next({});
    }

    public dismissInvitation(clinicId: string, specialtyId: string, message: string): void
    {
        this.emailState.set({
            status: RequestStatus.PENDING
        });

        this.dismissInvitation$.next({clinicId, specialtyId, message});
    }

    public updateSpecialityInfo(clinicId: string, specialtyId: string): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.updateSpecialityInfo$.next({clinicId, specialtyId});
    }

    public updateAppointmentTax(tax: number): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.updateAppointmentTax$.next({tax});
    }

    public clearClinician(): void
    {
        this.state.set({
            ...this.state(),
            user: null
        });
    }
}
